import React from "react";
import PropTypes from "prop-types";
import { Icon } from "@qgiv/core-react";
import { Accordion, ReceiptAccordionLabelContent } from "@qgiv/core-donor";
import ConnectedReceiptDetails from "./ConnectedReceiptDetails";
import ConnectedReceiptTransactionSummary from "./ConnectedReceiptTransactionSummary";
import ConnectedReceiptPaymentDetails from "./ConnectedReceiptPaymentDetails";
import ConnectedReceiptPersonalDetails from "./ConnectedReceiptPersonalDetails";
import ConnectedReceiptShippingDetails from "./ConnectedReceiptShippingDetails";
import ConnectedReceiptMatchingWidget from "../../Controls/ConnectedReceiptMatchingWidget";
import "./ReceiptSummary.scss";
import ConnectedReceiptDeliveryDetails from "./ConnectedReceiptDeliveryDetails";
import ConnectedAnonymityCallout from "./ConnectedAnonymityCallout";

const ReceiptSummary = ({
    accordionLabel = "Registration Details",
    accordionAdditionalText = "We emailed you a copy, plus a link to your fundraising page.",
    accordionAdditionalTextForNfps = "We emailed you a copy.",
    controlId,
    deliveryInfo = "",
    hideAmountCallout = "We won't show the amount of your gift on public campaign pages.",
    hideNameAndAmountCallout = "We won't show your name or the amount of your gift on public campaign pages.",
    hideNameCallout = "We won't show your name on public campaign pages.",
    isNFP = false,
    isPaymentDetailsVisible,
    offerShipping = false,
    printLinkLabel = "Print",
    receiptId,
    showAmount = true,
    showName = true,
    showMatchingWidget = false,
    showStoreDetails = false,
}) => {
    const handlePrintClick = (e) => {
        // Prevent clicks from bubbling up and closing the opened
        // accordion but don't trigger it if event was propagated
        // by the suspendedPrintClick function
        e && e.dispatchConfig && e.stopPropagation();

        const content = document.getElementById("receipt-summary");
        print(content);
    };

    return (
        <div className="receipt-summary">
            <Accordion>
                <section
                    label={accordionLabel}
                    renderLabelContent={({ isActive, label }) => {
                        const accordionLabelContentProps = {
                            handlePrintClick,
                            isActive,
                            label,
                            labelSubtext: isNFP
                                ? accordionAdditionalTextForNfps
                                : accordionAdditionalText,
                            printLinkLabel,
                        };
                        return (
                            <ReceiptAccordionLabelContent
                                {...accordionLabelContentProps}
                            />
                        );
                    }}
                >
                    <div className="grid" id="receipt-summary">
                        <ConnectedReceiptTransactionSummary
                            controlId={controlId}
                        />
                        <ConnectedReceiptDetails controlId={controlId} />
                        <ConnectedReceiptPersonalDetails
                            controlId={controlId}
                        />
                        {isPaymentDetailsVisible && (
                            <ConnectedReceiptPaymentDetails
                                controlId={controlId}
                            />
                        )}
                    </div>

                    {/* Privacy Options Callouts */}
                    <ConnectedAnonymityCallout controlId={controlId} />

                    {showStoreDetails && (
                        <div className="grid" id="receipt-store">
                            {offerShipping && (
                                <ConnectedReceiptShippingDetails
                                    controlId={controlId}
                                />
                            )}
                            {deliveryInfo.length > 0 && (
                                <ConnectedReceiptDeliveryDetails
                                    controlId={controlId}
                                />
                            )}
                        </div>
                    )}
                </section>
            </Accordion>
            {showMatchingWidget && (
                <ConnectedReceiptMatchingWidget receiptId={receiptId} />
            )}
        </div>
    );
};

ReceiptSummary.propTypes = {
    accordionLabel: PropTypes.string.isRequired,
    accordionAdditionalText: PropTypes.string,
    accordionAdditionalTextForNfps: PropTypes.string,
    controlId: PropTypes.number.isRequired,
    deliveryInfo: PropTypes.string.isRequired,
    hideAmountCallout: PropTypes.string.isRequired,
    hideNameAndAmountCallout: PropTypes.string.isRequired,
    hideNameCallout: PropTypes.string.isRequired,
    isNFP: PropTypes.bool.isRequired,
    isPaymentDetailsVisible: PropTypes.bool,
    offerShipping: PropTypes.bool.isRequired,
    printLinkLabel: PropTypes.string.isRequired,
    receiptId: PropTypes.string,
    showAmount: PropTypes.bool.isRequired,
    showMatchingWidget: PropTypes.bool,
    showName: PropTypes.bool.isRequired,
    showStoreDetails: PropTypes.bool.isRequired,
};

export default ReceiptSummary;
